/*
 * SAITECH FEATURE: selected-product quantity limits
 *
 * Design rule: do NOT replace Warehouse theme cart behavior.
 * The Warehouse theme remains responsible for Add to Cart, product:added,
 * mini-cart refresh, cart count refresh and cart-page rerendering.
 *
 * This file only:
 *  - validates /cart/add.js before Shopify receives the request;
 *  - clamps /cart/change.js and /cart/update.js quantities when needed;
 *  - blocks invalid picker/cart/checkout actions;
 *  - opens the existing request-more-quantity popup/form.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-saitech-quantity-limit-root]';
  var CONFIG_SELECTOR = '#SaitechQuantityLimitConfig';
  var MODAL_SELECTOR = '[data-saitech-quantity-limit-modal]';
  var LIMITED_FORM_CLASS = 'saitech-quantity-limited-product';
  var CHECKOUT_BYPASS = 'data-saitech-quantity-checkout-bypass';
  var CHECKOUT_FORM_BYPASS = 'data-saitech-quantity-checkout-form-bypass';
  var ORIGINAL_FETCH_KEY = '__saitechOriginalFetch';

  var state = {
    config: null,
    cart: null,
    cartPromise: null,
    installed: false,
    lastFocusedElement: null
  };

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function normalizeConfig(raw) {
    var config = raw || {};
    var products = Array.isArray(config.products) ? config.products : [];
    var productById = {};
    var variantToProduct = {};

    products.forEach(function (product) {
      if (!product || product.id == null) return;

      var normalized = Object.assign({}, product);
      normalized.id = String(product.id);
      normalized.limit = Math.max(1, parseInt(product.limit, 10) || 1);
      normalized.variantIds = Array.isArray(product.variantIds)
        ? product.variantIds.map(String)
        : [];

      productById[normalized.id] = normalized;
      normalized.variantIds.forEach(function (variantId) {
        variantToProduct[String(variantId)] = normalized.id;
      });
    });

    config.productById = productById;
    config.variantToProduct = variantToProduct;
    config.hasLimitedProducts = Object.keys(productById).length > 0;
    return config;
  }

  function getConfig() {
    if (state.config) return state.config;

    var root = getRoot();
    var element = root ? root.querySelector(CONFIG_SELECTOR) : null;
    if (!element) return null;

    try {
      state.config = normalizeConfig(JSON.parse(element.textContent));
      return state.config;
    } catch (error) {
      console.error('[Saitech quantity limit] Invalid configuration JSON.', error);
      return null;
    }
  }

  function activeConfig() {
    var config = getConfig();
    return config && config.enabled && config.hasLimitedProducts ? config : null;
  }

  function productForId(config, productId) {
    if (!config || productId == null) return null;
    return config.productById[String(productId)] || null;
  }

  function productForVariant(config, variantId) {
    if (!config || variantId == null) return null;
    var productId = config.variantToProduct[String(variantId)];
    return productId ? config.productById[productId] : null;
  }

  function productForCartItem(config, item) {
    if (!item) return null;
    return productForId(config, item.product_id) || productForVariant(config, item.variant_id || item.id);
  }

  function productQuantity(cart, productId) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce(function (total, item) {
      return String(item.product_id) === String(productId)
        ? total + Math.max(0, Number(item.quantity) || 0)
        : total;
    }, 0);
  }

  function productQuantityExcludingLine(cart, productId, lineIndex) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce(function (total, item, index) {
      if (index === lineIndex) return total;
      return String(item.product_id) === String(productId)
        ? total + Math.max(0, Number(item.quantity) || 0)
        : total;
    }, 0);
  }

  function routesUrl(name, fallback) {
    return String(window.routes && window.routes[name] ? window.routes[name] : fallback);
  }

  function cartJsonUrl() {
    return routesUrl('cartUrl', '/cart').replace(/\.js$/, '').replace(/\/$/, '') + '.js';
  }

  function originalFetch() {
    return window[ORIGINAL_FETCH_KEY] || window.fetch.bind(window);
  }

  function fetchCart(force) {
    if (!force && state.cart) return Promise.resolve(state.cart);
    if (state.cartPromise) return state.cartPromise;

    var url = cartJsonUrl();
    url += (url.indexOf('?') === -1 ? '?' : '&') + '_saitech=' + Date.now();

    state.cartPromise = originalFetch()(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to read the Shopify cart.');
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        return cart;
      })
      .catch(function (error) {
        console.error('[Saitech quantity limit] Cart check failed.', error);
        return null;
      })
      .then(function (cart) {
        state.cartPromise = null;
        return cart;
      });

    return state.cartPromise;
  }

  function refreshCartSoon() {
    state.cart = null;
    window.setTimeout(function () {
      fetchCart(true);
    }, 0);
  }

  function replaceTokens(text, product) {
    return String(text || '')
      .replace(/\[limit\]/gi, product ? String(product.limit) : '')
      .replace(/\[product\]/gi, product && product.title ? product.title : 'this product');
  }

  function productUrl(product) {
    if (product && product.url) {
      try {
        return new URL(product.url, window.location.origin).href;
      } catch (error) {
        return String(product.url);
      }
    }

    if (product && product.handle) return window.location.origin + '/products/' + product.handle;
    return window.location.href;
  }

  function openPopup(config, product, context) {
    var root = getRoot();
    var modal = root ? root.querySelector(MODAL_SELECTOR) : null;
    if (!modal || !product) return;

    context = context || {};

    var title = modal.querySelector('.saitech-quantity-limit__title');
    var message = modal.querySelector('.saitech-quantity-limit__message');
    var requestButton = modal.querySelector('[data-saitech-quantity-limit-request]');
    var continueButton = modal.querySelector('.saitech-quantity-limit__continue');

    if (title) title.textContent = replaceTokens(config.popupTitle, product);
    if (message) message.textContent = replaceTokens(config.popupMessage, product);
    if (continueButton) continueButton.textContent = config.continueButtonLabel || 'Continue Shopping';

    if (requestButton) {
      requestButton.textContent = config.requestButtonLabel || 'Request More Quantity';
      requestButton.dataset.productTitle = product.title || '';
      requestButton.dataset.productId = product.id || '';
      requestButton.dataset.productHandle = product.handle || '';
      requestButton.dataset.productUrl = productUrl(product);
      requestButton.dataset.variantId = context.variantId ? String(context.variantId) : '';
      requestButton.dataset.variantTitle = context.variantTitle || '';
      requestButton.dataset.sku = context.sku || '';
      requestButton.dataset.quantityLimit = String(product.limit);
      requestButton.dataset.requestedQuantity = String(
        Math.max(product.limit + 1, Number(context.requestedQuantity) || 0)
      );
    }

    state.lastFocusedElement = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('saitech-quantity-limit-is-open');

    var closeButton = modal.querySelector('[data-saitech-quantity-limit-close]');
    if (closeButton) closeButton.focus();
  }

  function closePopup() {
    var root = getRoot();
    var modal = root ? root.querySelector(MODAL_SELECTOR) : null;
    if (!modal) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('saitech-quantity-limit-is-open');

    if (state.lastFocusedElement && typeof state.lastFocusedElement.focus === 'function') {
      state.lastFocusedElement.focus();
    }
  }

  function openRequestForm(button) {
    closePopup();

    if (window.SaitechQuantityRequestForm && typeof window.SaitechQuantityRequestForm.open === 'function') {
      window.SaitechQuantityRequestForm.open({
        productTitle: button.dataset.productTitle || 'Selected product',
        productId: button.dataset.productId || '',
        productHandle: button.dataset.productHandle || '',
        productUrl: button.dataset.productUrl || window.location.href,
        variantId: button.dataset.variantId || '',
        variantTitle: button.dataset.variantTitle || '',
        sku: button.dataset.sku || '',
        limit: Number(button.dataset.quantityLimit || 0),
        requestedQuantity: Number(button.dataset.requestedQuantity || 0)
      });
      return;
    }

    console.error('[Saitech quantity limit] Request form is unavailable.');
  }

  function popupForItem(config, product, item, requestedQuantity) {
    openPopup(config, product, {
      variantId: item ? item.variant_id || item.id || '' : '',
      variantTitle: item ? item.variant_title || '' : '',
      sku: item ? item.sku || '' : '',
      requestedQuantity: requestedQuantity
    });
  }

  function responseWithLimitError(product) {
    var description = 'Maximum quantity for ' + (product.title || 'this product') + ' is ' + product.limit + '.';

    return new Response(JSON.stringify({
      status: 422,
      message: 'Quantity limit reached',
      description: description
    }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function requestPath(input) {
    try {
      var raw = typeof input === 'string' ? input : input && input.url ? input.url : String(input || '');
      return new URL(raw, window.location.origin).pathname;
    } catch (error) {
      return String(input || '').split('?')[0];
    }
  }

  function cloneOptions(options) {
    var clone = {};
    Object.keys(options || {}).forEach(function (key) {
      clone[key] = options[key];
    });
    return clone;
  }

  function parseRequestBody(body) {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        return { type: 'json', value: JSON.parse(body) };
      } catch (error) {
        try {
          var params = new URLSearchParams(body);
          var object = {};
          params.forEach(function (value, key) { object[key] = value; });
          return { type: 'urlencoded', value: object };
        } catch (ignored) {
          return null;
        }
      }
    }

    if (window.FormData && body instanceof FormData) {
      var formObject = {};
      body.forEach(function (value, key) { formObject[key] = value; });
      return { type: 'formdata', value: formObject, original: body };
    }

    if (window.URLSearchParams && body instanceof URLSearchParams) {
      var paramObject = {};
      body.forEach(function (value, key) { paramObject[key] = value; });
      return { type: 'urlsearchparams', value: paramObject, original: body };
    }

    return null;
  }

  function withUpdatedBody(options, parsedBody, data) {
    var next = cloneOptions(options);

    if (!parsedBody || parsedBody.type === 'json') {
      next.body = JSON.stringify(data);
      return next;
    }

    if (parsedBody.type === 'urlencoded' || parsedBody.type === 'urlsearchparams') {
      var params = new URLSearchParams();
      Object.keys(data).forEach(function (key) {
        if (data[key] != null) params.set(key, String(data[key]));
      });
      next.body = params.toString();
      return next;
    }

    if (parsedBody.type === 'formdata') {
      var formData = new FormData();
      Object.keys(data).forEach(function (key) {
        if (data[key] != null) formData.append(key, data[key]);
      });
      next.body = formData;
      return next;
    }

    return next;
  }

  function normalizeAddItems(body) {
    if (!body) return [];

    if (Array.isArray(body.items)) {
      return body.items.map(function (item) {
        return {
          variantId: String(item.id || item.variant_id || ''),
          quantity: Math.max(1, parseInt(item.quantity, 10) || 1)
        };
      });
    }

    if (body.id != null) {
      return [{
        variantId: String(body.id),
        quantity: Math.max(1, parseInt(body.quantity, 10) || 1)
      }];
    }

    return [];
  }

  function validateAddRequest(config, body, cart) {
    var additionsByProduct = {};
    var firstContextByProduct = {};

    normalizeAddItems(body).forEach(function (addition) {
      var product = productForVariant(config, addition.variantId);
      if (!product) return;

      additionsByProduct[product.id] = (additionsByProduct[product.id] || 0) + addition.quantity;
      if (!firstContextByProduct[product.id]) firstContextByProduct[product.id] = addition;
    });

    var productIds = Object.keys(additionsByProduct);
    for (var i = 0; i < productIds.length; i += 1) {
      var productId = productIds[i];
      var product = productForId(config, productId);
      var requestedTotal = productQuantity(cart, productId) + additionsByProduct[productId];

      if (requestedTotal > product.limit) {
        return {
          product: product,
          context: firstContextByProduct[productId],
          requestedQuantity: requestedTotal
        };
      }
    }

    return null;
  }

  function interceptAdd(input, options, config, nativeFetch) {
    var parsed = parseRequestBody(options && options.body);
    if (!parsed) return nativeFetch(input, options);

    var hasLimitedItem = normalizeAddItems(parsed.value).some(function (addition) {
      return !!productForVariant(config, addition.variantId);
    });
    if (!hasLimitedItem) return nativeFetch(input, options);

    return fetchCart(true).then(function (cart) {
      if (!cart) return nativeFetch(input, options);

      var violation = validateAddRequest(config, parsed.value, cart);
      if (violation) {
        openPopup(config, violation.product, {
          variantId: violation.context ? violation.context.variantId : '',
          requestedQuantity: violation.requestedQuantity
        });
        return responseWithLimitError(violation.product);
      }

      return nativeFetch(input, options).then(function (response) {
        if (response.ok) state.cart = null;
        return response;
      });
    });
  }

  function interceptChange(input, options, config, nativeFetch) {
    var parsed = parseRequestBody(options && options.body);
    if (!parsed || !parsed.value) return nativeFetch(input, options);

    var body = parsed.value;
    var line = parseInt(body.line, 10);
    var desired = Math.max(0, parseInt(body.quantity, 10) || 0);
    if (!line) return nativeFetch(input, options);

    return fetchCart(true).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return nativeFetch(input, options);

      var lineIndex = line - 1;
      var item = cart.items[lineIndex];
      var product = productForCartItem(config, item);
      if (!item || !product) {
        return nativeFetch(input, options).then(function (response) {
          if (response.ok) state.cart = null;
          return response;
        });
      }

      var otherQuantity = productQuantityExcludingLine(cart, product.id, lineIndex);
      var maximumForLine = Math.max(0, product.limit - otherQuantity);
      var adjusted = Math.min(desired, maximumForLine);
      var nextOptions = options;

      if (desired > maximumForLine) {
        popupForItem(config, product, item, otherQuantity + desired);
        var nextBody = Object.assign({}, body, { quantity: adjusted });
        nextOptions = withUpdatedBody(options, parsed, nextBody);
      }

      return nativeFetch(input, nextOptions).then(function (response) {
        if (response.ok) state.cart = null;
        return response;
      });
    });
  }

  function interceptUpdate(input, options, config, nativeFetch) {
    var parsed = parseRequestBody(options && options.body);
    if (!parsed || !parsed.value || !parsed.value.updates) return nativeFetch(input, options);

    return fetchCart(true).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return nativeFetch(input, options);

      var body = parsed.value;
      var updates = Object.assign({}, body.updates);
      var changed = false;

      Object.keys(updates).forEach(function (key) {
        var lineIndex = /^\d+$/.test(key) ? parseInt(key, 10) - 1 : -1;
        if (lineIndex < 0 || !cart.items[lineIndex]) return;

        var item = cart.items[lineIndex];
        var product = productForCartItem(config, item);
        if (!product) return;

        var desired = Math.max(0, parseInt(updates[key], 10) || 0);
        var otherQuantity = productQuantityExcludingLine(cart, product.id, lineIndex);
        var maximumForLine = Math.max(0, product.limit - otherQuantity);

        if (desired > maximumForLine) {
          updates[key] = maximumForLine;
          changed = true;
          popupForItem(config, product, item, otherQuantity + desired);
        }
      });

      var nextOptions = changed
        ? withUpdatedBody(options, parsed, Object.assign({}, body, { updates: updates }))
        : options;

      return nativeFetch(input, nextOptions).then(function (response) {
        if (response.ok) state.cart = null;
        return response;
      });
    });
  }

  function installFetchGuard(config) {
    if (window[ORIGINAL_FETCH_KEY]) return;

    var nativeFetch = window.fetch.bind(window);
    window[ORIGINAL_FETCH_KEY] = nativeFetch;

    window.fetch = function (input, options) {
      var path = requestPath(input);

      if (/\/cart\/add\.js$/.test(path)) {
        return interceptAdd(input, options || {}, config, nativeFetch);
      }

      if (/\/cart\/change\.js$/.test(path)) {
        return interceptChange(input, options || {}, config, nativeFetch);
      }

      if (/\/cart\/update\.js$/.test(path)) {
        return interceptUpdate(input, options || {}, config, nativeFetch);
      }

      return nativeFetch(input, options);
    };
  }

  function isProductForm(form) {
    return !!(form && form.tagName === 'FORM' && String(form.getAttribute('action') || '').indexOf('/cart/add') !== -1);
  }

  function formVariantId(form) {
    var input = form ? form.querySelector('[name="id"]') : null;
    return input ? String(input.value || '') : '';
  }

  function formQuantity(form) {
    var input = form ? form.querySelector('[name="quantity"]') : null;
    return Math.max(1, parseInt(input ? input.value : '1', 10) || 1);
  }

  function syncLimitedForm(form, config) {
    if (!isProductForm(form)) return;
    var product = productForVariant(config, formVariantId(form));
    form.classList.toggle(LIMITED_FORM_CLASS, !!product);
  }

  function syncAllLimitedForms(config) {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      syncLimitedForm(form, config);
    });
  }

  function productPickerDetails(target, config) {
    var form = target ? target.closest('form[action*="/cart/add"]') : null;
    if (!isProductForm(form)) return null;

    var variantId = formVariantId(form);
    var product = productForVariant(config, variantId);
    if (!product) return null;

    var input = form.querySelector('.quantity-selector--product [name="quantity"], [name="quantity"]');
    return input ? { form: form, input: input, product: product, variantId: variantId } : null;
  }

  function remainingForProduct(product) {
    if (!state.cart) return product.limit;
    return Math.max(0, product.limit - productQuantity(state.cart, product.id));
  }

  function handlePickerIncrease(event, button, config) {
    var details = productPickerDetails(button, config);
    if (!details) return;

    var current = Math.max(1, parseInt(details.input.value, 10) || 1);
    var requestedLocal = current + 1;
    var remaining = remainingForProduct(details.product);

    if (requestedLocal > remaining) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      openPopup(config, details.product, {
        variantId: details.variantId,
        requestedQuantity: productQuantity(state.cart, details.product.id) + requestedLocal
      });
    }
  }

  function enforcePickerInput(input, config, showPopup) {
    var details = productPickerDetails(input, config);
    if (!details) return;

    var entered = parseInt(input.value, 10);
    if (!Number.isFinite(entered) || entered < 1) return;

    var remaining = remainingForProduct(details.product);
    if (remaining <= 0) {
      if (entered > 1) input.value = '1';
      if (showPopup) {
        openPopup(config, details.product, {
          variantId: details.variantId,
          requestedQuantity: productQuantity(state.cart, details.product.id) + entered
        });
      }
      return;
    }

    if (entered > remaining) {
      input.value = String(remaining);
      input.setAttribute('size', Math.max(String(remaining).length, 2));
      if (showPopup) {
        openPopup(config, details.product, {
          variantId: details.variantId,
          requestedQuantity: productQuantity(state.cart, details.product.id) + entered
        });
      }
    }
  }

  function cartLineFromControl(control) {
    var line = parseInt(control && control.getAttribute('data-line'), 10);
    if (!line || !state.cart || !state.cart.items || !state.cart.items[line - 1]) return null;
    return { line: line, lineIndex: line - 1, item: state.cart.items[line - 1] };
  }

  function guardPageCartIncrease(event, control, config) {
    if (!window.theme || window.theme.cartType !== 'page') return;

    var lineData = cartLineFromControl(control);
    if (!lineData) return;

    var product = productForCartItem(config, lineData.item);
    if (!product) return;

    var desired = Math.max(0, parseInt(control.getAttribute('data-quantity'), 10) || 0);
    var otherQuantity = productQuantityExcludingLine(state.cart, product.id, lineData.lineIndex);

    if (otherQuantity + desired > product.limit) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      popupForItem(config, product, lineData.item, otherQuantity + desired);
    }
  }

  function guardPageCartManualInput(input, config) {
    if (!window.theme || window.theme.cartType !== 'page') return;

    var lineData = cartLineFromControl(input);
    if (!lineData) return;

    var product = productForCartItem(config, lineData.item);
    if (!product) return;

    var desired = Math.max(0, parseInt(input.value, 10) || 0);
    var otherQuantity = productQuantityExcludingLine(state.cart, product.id, lineData.lineIndex);
    var maximumForLine = Math.max(0, product.limit - otherQuantity);

    if (desired > maximumForLine) {
      input.value = String(maximumForLine);
      input.setAttribute('size', Math.max(String(maximumForLine).length, 2));
      popupForItem(config, product, lineData.item, otherQuantity + desired);
    }
  }

  function findCartViolation(cart, config) {
    var ids = Object.keys(config.productById);

    for (var i = 0; i < ids.length; i += 1) {
      var product = config.productById[ids[i]];
      var total = productQuantity(cart, product.id);
      if (total > product.limit) {
        var item = cart.items.find(function (cartItem) {
          return String(cartItem.product_id) === String(product.id);
        });
        return { product: product, item: item || null, requestedQuantity: total };
      }
    }

    return null;
  }

  function validateCheckout(config) {
    return fetchCart(true).then(function (cart) {
      if (!cart) return false;
      var violation = findCartViolation(cart, config);
      if (!violation) return true;
      popupForItem(config, violation.product, violation.item, violation.requestedQuantity);
      return false;
    });
  }

  function replayCheckoutClick(control) {
    control.setAttribute(CHECKOUT_BYPASS, '1');
    control.click();
  }

  function handleClick(event) {
    var requestButton = event.target.closest('[data-saitech-quantity-limit-request]');
    if (requestButton) {
      event.preventDefault();
      openRequestForm(requestButton);
      return;
    }

    var closeButton = event.target.closest('[data-saitech-quantity-limit-close]');
    if (closeButton) {
      event.preventDefault();
      closePopup();
      return;
    }

    var modal = event.target.closest(MODAL_SELECTOR);
    if (modal && event.target === modal) {
      event.preventDefault();
      closePopup();
      return;
    }

    var config = activeConfig();
    if (!config) return;

    var pickerIncrease = event.target.closest('[data-action="increase-picker-quantity"]');
    if (pickerIncrease) {
      handlePickerIncrease(event, pickerIncrease, config);
      return;
    }

    var cartIncrease = event.target.closest('[data-action="increase-quantity"]');
    if (cartIncrease) {
      guardPageCartIncrease(event, cartIncrease, config);
      return;
    }

    var checkout = event.target.closest('[name="checkout"], a[href*="/checkout"], form[action*="/checkout"] button[type="submit"]');
    if (!checkout || checkout.closest(ROOT_SELECTOR)) return;

    if (checkout.hasAttribute(CHECKOUT_BYPASS)) {
      checkout.removeAttribute(CHECKOUT_BYPASS);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    validateCheckout(config).then(function (allowed) {
      if (allowed) replayCheckoutClick(checkout);
    });
  }

  function handleSubmit(event) {
    var form = event.target;
    var config = activeConfig();
    if (!config || !form || form.tagName !== 'FORM') return;

    if (form.hasAttribute(CHECKOUT_FORM_BYPASS)) {
      form.removeAttribute(CHECKOUT_FORM_BYPASS);
      return;
    }

    var submitter = event.submitter || null;
    var action = String(form.getAttribute('action') || '');
    var isCheckoutSubmit =
      action.indexOf('/checkout') !== -1 ||
      (submitter && submitter.getAttribute('name') === 'checkout');

    if (isCheckoutSubmit) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      validateCheckout(config).then(function (allowed) {
        if (!allowed) return;
        form.setAttribute(CHECKOUT_FORM_BYPASS, '1');
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter || undefined);
        } else {
          form.submit();
        }
      });
      return;
    }

    if (!isProductForm(form)) return;

    var product = productForVariant(config, formVariantId(form));
    if (!product) return;

    /*
     * Warehouse normally handles a button click itself. A submit event here is
     * typically keyboard/native submission, so validate it and then hand the
     * successful action back to Warehouse when an Add to Cart button exists.
     */
    var quantity = formQuantity(form);
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    fetchCart(true).then(function (cart) {
      if (!cart) {
        form.submit();
        return;
      }

      var requestedTotal = productQuantity(cart, product.id) + quantity;
      if (requestedTotal > product.limit) {
        openPopup(config, product, {
          variantId: formVariantId(form),
          requestedQuantity: requestedTotal
        });
        return;
      }

      var addButton = form.querySelector('[data-action="add-to-cart"]');
      if (addButton && window.theme && window.theme.cartType !== 'page') {
        addButton.click();
      } else {
        form.submit();
      }
    });
  }

  function handleInput(event) {
    var config = activeConfig();
    if (!config) return;

    var input = event.target;
    if (input && input.matches('.quantity-selector--product [name="quantity"]')) {
      enforcePickerInput(input, config, true);
    }
  }

  function handleChange(event) {
    var config = activeConfig();
    if (!config) return;

    var input = event.target;
    if (!input) return;

    var form = input.closest('form[action*="/cart/add"]');
    if (form) {
      syncLimitedForm(form, config);
      if (input.matches('.quantity-selector--product [name="quantity"]')) {
        enforcePickerInput(input, config, true);
      }
      return;
    }

    if (input.matches('.quantity-selector:not(.quantity-selector--product) .quantity-selector__value[data-line]')) {
      guardPageCartManualInput(input, config);
    }
  }

  function install() {
    if (state.installed) return;
    state.installed = true;

    var config = activeConfig();
    if (!config) return;

    installFetchGuard(config);
    syncAllLimitedForms(config);
    fetchCart(true);

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('input', handleInput, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closePopup();
    }, true);

    document.documentElement.addEventListener('product:added', refreshCartSoon);
    document.documentElement.addEventListener('cart:rerendered', refreshCartSoon);
    document.documentElement.addEventListener('cart:refresh', refreshCartSoon);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }
})();
